import { useMemo } from 'react';

/**
 * Starfield component generates an ambient cosmic background 
 * with anti-banding SVG dithering noise and smooth radial nebulas
 * engineered to look crisp on both SDR/1080p and High-DPI/OLED screens.
 */
const Starfield = () => {
  // Generate deterministic stars list so it stays stable across renders
  const stars = useMemo(() => {
    return Array.from({ length: 60 }).map((_, i) => ({
      id: i,
      top: `${Math.floor(Math.random() * 100)}%`,
      left: `${Math.floor(Math.random() * 100)}%`,
      size: Math.random() > 0.7 ? 3 : Math.random() > 0.4 ? 2 : 1.5,
      duration: `${(3.0 + Math.random() * 4).toFixed(2)}s`,
      delay: `${(Math.random() * 5).toFixed(2)}s`,
      opacity: (0.4 + Math.random() * 0.5).toFixed(2),
      color: Math.random() > 0.6 ? 'bg-fuchsia-200' : Math.random() > 0.3 ? 'bg-purple-200' : 'bg-pink-200'
    }));
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 bg-[#050409]">
      {/* Smooth GPU Radial Nebulas (Eliminates heavy CSS blur artifacting) */}
      <div 
        className="absolute inset-0 opacity-80"
        style={{
          background: `
            radial-gradient(circle 500px at 25% 15%, rgba(76, 29, 149, 0.25), transparent 70%),
            radial-gradient(circle 450px at 75% 35%, rgba(131, 24, 67, 0.18), transparent 70%),
            radial-gradient(circle 550px at 40% 85%, rgba(88, 28, 135, 0.22), transparent 70%)
          `
        }}
      />

      {/* SVG Film Grain Noise Overlay (Dithering layer that fixes 8-bit SDR monitor color banding) */}
      <div 
        className="absolute inset-0 opacity-[0.035] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />

      {/* Twinkling Crisp Stars Layer */}
      {stars.map((star) => (
        <span
          key={star.id}
          className={`absolute rounded-full ${star.color} animate-twinkle shadow-[0_0_6px_rgba(255,255,255,0.7)]`}
          style={{
            top: star.top,
            left: star.left,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            '--twinkle-duration': star.duration,
            '--twinkle-delay': star.delay
          }}
        />
      ))}
    </div>
  );
};

export default Starfield;
